import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchSettingsAccounts,
  fetchSettingsGroups,
  removeAccountFromStore,
  updateAccountInStore,
  removeGroupFromStore,
  updateGroupInStore,
  invalidateAccounts,
  invalidateGroups,
} from "../../store/settingsSlice";
import { getAuthTokenPayload, getOwnMenuList } from "../../Utils/index";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  Card,
  CardContent,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Tooltip,
  Collapse,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  AccountCircle as AccountIcon,
  Sort as SortIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ContentCopy as ContentCopyIcon, // ADDED: Clone icon
  Link as LinkIcon, // ADDED: Link icon
  WarningAmber as WarningAmberIcon,
  ErrorOutline as ErrorOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";

// Import components
import AddNewAccount from "./addNewAccount";
import AddNewGroup from "./addNewGroup";
import ViewDetailsModal from "./ViewDetailsModal"; // ADDED: Import the new component
import {
  editGroupOrAccount,
  removeAccount,
  removeGroup,
  closeClientAccount,
  getUsersAPI,
} from "../../Action/api";

export const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // ── Redux: accounts & groups data (cached across navigation) ──────────────
  const accounts      = useSelector((state) => state.settings.accounts);
  const groups        = useSelector((state) => state.settings.groups);
  const accountsStatus = useSelector((state) => state.settings.accountsStatus);
  const groupsStatus   = useSelector((state) => state.settings.groupsStatus);
  const accountsError  = useSelector((state) => state.settings.accountsError);
  const groupsError    = useSelector((state) => state.settings.groupsError);

  // Derived booleans – same semantics as the old useState flags
  const loadingAccounts = accountsStatus === "loading";
  const loadingGroups   = groupsStatus   === "loading";
  const accountsEmpty   = accountsStatus === "succeeded" && accounts.length === 0;
  const groupsEmpty     = groupsStatus   === "succeeded" && groups.length   === 0;
  const accountsFirstLoad = accountsStatus === "idle";
  const groupsFirstLoad   = groupsStatus   === "idle";
  const loading = loadingAccounts || loadingGroups;

  // Role-based access
  const roleName = getAuthTokenPayload()?.roleName;
  const isAdmin = roleName?.toLowerCase() === "admin";

  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [accountDialog, setAccountDialog] = useState(false);
  const [groupDialog, setGroupDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [isCloning, setIsCloning] = useState(false); // ADDED: Track if we're cloning
  const [initialProvider, setInitialProvider] = useState("");

  // Open Add Account dialog from navigation state (e.g. Dashboard "Connect" button)
  useEffect(() => {
    if (location.state?.openAddAccount && canAddSettings && !isAccountLimitReached) {
      setInitialProvider(location.state.provider || "");
      setEditingAccount(null);
      setIsCloning(false);
      setAccountDialog(true);
      // Clear the state so re-visiting doesn't re-open
      navigate(location.pathname, { replace: true, state: {} });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // API Data States — now in Redux (see selectors above)
  // const [accounts] / [groups] / [loadingAccounts] etc. removed
  // API errors (accountsError / groupsError) come from Redux

  // Edit operation states
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState("");

  // Delete operation states - ADDED
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteSuccess, setDeleteSuccess] = useState("");

  // Delete confirmation states - ADDED
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(""); // 'account' or 'group'

  // Close Account (Danger Zone)
  const [closeMyAccountOpen, setCloseMyAccountOpen] = useState(false);
  const [closeAccountConfirmText, setCloseAccountConfirmText] = useState("");
  const [closingAccount, setClosingAccount] = useState(false);
  const [closeAccountError, setCloseAccountError] = useState("");
  const [dangerZoneOpen, setDangerZoneOpen] = useState(false);

  // ADDED: Additional states — now derived from Redux (see selectors above)
  // accountsEmpty / groupsEmpty / accountsFirstLoad / groupsFirstLoad removed

  // ADDED: Sorting states
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc"); // 'asc' or 'desc'

  // New state variables for view dialog
  const [viewDialog, setViewDialog] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);
  const [viewType, setViewType] = useState(""); // 'account' or 'group'

  // Permission check using menuList from JWT token
  const checkUserPermission = (menuId, action = "read") => {
    try {
      const menuList = getOwnMenuList();
      const menu = menuList.find((m) => m.menuId === menuId);

      if (!menu) return false;

      switch (action) {
        case "read":
        case "view":
          return menu.readAccess === "yes";
        case "write":
        case "add":
        case "edit":
        case "delete":
          return menu.writeAccess === "yes";
        default:
          return false;
      }
    } catch (error) {
      console.error("Error checking permissions:", error);
      return false;
    }
  };

  // Permission checks for Settings menu (MENU-008)
  const canViewSettings = checkUserPermission("MENU-008", "read");
  const canAddSettings = checkUserPermission("MENU-008", "write");
  const canEditSettings = checkUserPermission("MENU-008", "write");
  const canDeleteSettings = checkUserPermission("MENU-008", "write");

  // Account & group limits from MENU-008 token entry
  const getSettingsLimits = () => {
    const menuList = getOwnMenuList();
    const menu = menuList.find((m) => m.menuId === "MENU-008");
    const accountLimit = menu?.accountLimit !== undefined ? Number(menu.accountLimit) : null;
    const groupLimit   = menu?.groupLimit   !== undefined ? Number(menu.groupLimit)   : null;
    return { accountLimit, groupLimit };
  };
  const { accountLimit, groupLimit } = getSettingsLimits();
  const isAccountLimitReached = accountLimit !== null && accounts.length >= accountLimit;
  const isGroupLimitReached   = groupLimit   !== null && groups.length   >= groupLimit;

  // Dropdown options

  // Phase2
  // const accountTypes = ['Social Media', 'Email', 'SMS'];

  // Phase1
  const accountTypes = ["Social Media"];

  // Phase2
  // const providers = {
  //   'Social Media': ['Facebook', 'Instagram', 'LinkedIn', 'Twitter', 'YouTube'],
  //   'Email': ['SendGrid', 'Mailchimp', 'Gmail SMTP', 'Outlook SMTP', 'AWS SES'],
  //   'SMS': ['Twilio', 'AWS SNS', 'Nexmo', 'TextMagic', 'ClickSend']
  // };

  const providers = {
    "Social Media": ["Facebook", "Instagram", "LinkedIn"],
  };
  // Phase2
  // const groupTypes = ['Social Media', 'Email', 'SMS', 'Mixed'];
  const groupTypes = ["Social Media"];

  // ── Data-fetch helpers ─────────────────────────────────────────────────────
  // These dispatch Redux thunks. The thunk only runs the API call; all state
  // (accounts, groups, loading flags) lives in Redux so it survives navigation.

  /**
   * Dispatch a fresh accounts fetch.
   * Pass force=true to bypass the Redux cache (e.g. Refresh button).
   */
  const fetchAccountsData = (force = false) => {
    if (force) dispatch(invalidateAccounts());
    dispatch(fetchSettingsAccounts());
  };

  /**
   * Dispatch a fresh groups fetch.
   * Pass force=true to bypass the Redux cache (e.g. Refresh button).
   */
  const fetchGroupsData = (force = false) => {
    if (force) dispatch(invalidateGroups());
    dispatch(fetchSettingsGroups());
  };

  /** Refresh BOTH lists unconditionally (Refresh button). */
  const fetchData = () => {
    dispatch(fetchSettingsAccounts());
    dispatch(fetchSettingsGroups());
  };

  // processAccountsData / processGroupsData have been moved into settingsSlice.js

  // Load data on component mount – skip if already in Redux (navigated back)
  useEffect(() => {
    if (!canViewSettings) return;
    if (accountsStatus === "idle") dispatch(fetchSettingsAccounts());
    if (groupsStatus   === "idle") dispatch(fetchSettingsGroups());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewSettings]);

  // Debug logs
  useEffect(() => {
    console.log("Settings Permission Check:", {
      canView: canViewSettings,
      canAdd: canAddSettings,
      canEdit: canEditSettings,
      canDelete: canDeleteSettings,
      menuList: getOwnMenuList(),
    });
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const openAccountDialog = (account = null, clone = false) => {
    if (!canAddSettings && !canEditSettings) return;
    // Block creating a brand-new account OR cloning once the limit is hit
    if (isAccountLimitReached && (!account || clone)) return;

    if (clone && account) {
      // ADDED: Clone mode - create a copy with modified name and no ID
      const clonedAccount = {
        ...account,
        accountId: undefined, // Remove ID for new account
        id: undefined,
        accountName: `${account.accountName} (Copy)`,
        // Keep all other fields for easy modification
      };
      setEditingAccount(clonedAccount);
      setIsCloning(true);
    } else {
      setEditingAccount(account);
      setIsCloning(false);
    }
    if (!account) setInitialProvider(""); // reset when manually opening fresh
    setAccountDialog(true);
    setUpdateError("");
    setUpdateSuccess("");
  };

  const closeAccountDialog = () => {
    setAccountDialog(false);
    setEditingAccount(null);
    setIsCloning(false); // ADDED: Reset clone flag
    setInitialProvider("");
    setUpdateError("");
    setUpdateSuccess("");
  };

  const handleCloseAccount = async () => {
    setCloseMyAccountOpen(false);
    setClosingAccount(true);
    setCloseAccountError("");
    try {
      const payload = getAuthTokenPayload();
      const clientId = payload?.clientId;

      // Fetch all users and collect their IDs
      let userIds = [];
      try {
        const usersResponse = await getUsersAPI();
        if (usersResponse?.data?.status && Array.isArray(usersResponse.data.users)) {
          userIds = usersResponse.data.users.map((u) => u.userId).filter(Boolean);
        }
      } catch (_) {}
      // Fallback to current user if API fails or returns empty
      if (userIds.length === 0) {
        const fallbackId = payload?.userId || payload?.staffId;
        if (fallbackId) userIds = [fallbackId];
      }

      const result = await closeClientAccount(clientId, userIds);

      if (result.success) {
        // Success — clear session and redirect to login
        localStorage.clear();
        navigate("/");
      } else {
        setCloseAccountError(result.message || "Failed to close account. Please try again.");
        setClosingAccount(false);
        setCloseAccountConfirmText("");
        setCloseMyAccountOpen(true);
      }
    } catch (err) {
      console.error("Close account error:", err);
      setCloseAccountError("An unexpected error occurred. Please try again.");
      setClosingAccount(false);
      setCloseAccountConfirmText("");
      setCloseMyAccountOpen(true);
    }
  };

  const openGroupDialog = (group = null, clone = false) => {
    if (!canAddSettings && !canEditSettings) return;
    // Block creating a brand-new group OR cloning once the limit is hit
    if (isGroupLimitReached && (!group || clone)) return;

    if (clone && group) {
      // ADDED: Clone mode - create a copy with modified name and no ID
      const clonedGroup = {
        ...group,
        groupId: undefined, // Remove ID for new group
        id: undefined,
        groupName: `${group.groupName} (Copy)`,
        // Keep all other fields for easy modification
      };
      setEditingGroup(clonedGroup);
      setIsCloning(true);
    } else {
      setEditingGroup(group);
      setIsCloning(false);
    }
    setGroupDialog(true);
    setUpdateError("");
    setUpdateSuccess("");
  };

  const closeGroupDialog = () => {
    setGroupDialog(false);
    setEditingGroup(null);
    setIsCloning(false); // ADDED: Reset clone flag
    setUpdateError("");
    setUpdateSuccess("");
  };

  // ADDED: Open view dialog
  const openViewDialog = (item, type) => {
    if (!canViewSettings) return;

    setViewingItem(item);
    setViewType(type);
    setViewDialog(true);
  };

  // ADDED: Close view dialog
  const closeViewDialog = () => {
    setViewDialog(false);
    setViewingItem(null);
    setViewType("");
  };

  // ADDED: Handle edit from view modal
  const handleEditFromView = (item, type) => {
    if (type === "account") {
      openAccountDialog(item);
    } else {
      openGroupDialog(item);
    }
  };

  // UPDATED: Handle account submission with API integration
  const handleAccountSubmit = async (accountData, action) => {
    if (!canAddSettings && !canEditSettings) return;

    setUpdateError("");
    setUpdateSuccess("");

    // UPDATED: Treat cloning as adding a new account
    if (action === "edit" && !isCloning) {
      setUpdating(true);
      try {
        // Prepare API payload for account editing
        const editPayload = {
          entityType: accountData.entityType || "Account",
          accountId: accountData.accountId,
          groupId: accountData.groupId || "",
          type: accountData.type,
          provider: accountData.provider,
          accountName: accountData.accountName,
          connectedBy: accountData.connectedBy,
          status: accountData.status,
          notes: accountData.notes || "", // ADDED: Include notes in API payload
          editMode: "EditAccount",

          // Add type-specific fields
          ...(accountData.type === "Social Media" && {
            username: accountData.username,
            accessToken: accountData.accessToken,
            tokenExpiry: accountData.tokenExpiry,
          }),
          ...(accountData.type === "Email" && {
            apiKey: accountData.apiKey,
            smtpHost: accountData.smtpHost,
            smtpPort: accountData.smtpPort,
            smtpUsername: accountData.smtpUsername,
            smtpPassword: accountData.smtpPassword,
            fromEmail: accountData.fromEmail,
            fromName: accountData.fromName,
            replyToEmail: accountData.replyToEmail,
          }),
          ...(accountData.type === "SMS" && {
            authToken: accountData.authToken,
            senderId: accountData.senderId,
            country: accountData.country,
            region: accountData.region,
          }),
        };

        console.log("Updating account with payload:", editPayload);

        const result = await editGroupOrAccount(editPayload);

        if (result.success) {
          setUpdateSuccess(
            `Account "${accountData.accountName}" updated successfully`
          );
          // Update Redux store in-place — no API re-fetch needed
          dispatch(updateAccountInStore({ ...accountData }));
          setTimeout(() => {
            closeAccountDialog();
          }, 1500);
        } else {
          setUpdateError(result.message);
        }
      } catch (error) {
        console.error("Error updating account:", error);
        setUpdateError("Failed to update account. Please try again.");
      } finally {
        setUpdating(false);
      }
    } else {
      // Add new account or clone — force-refresh from API (server assigns the new ID)
      setUpdateSuccess(
        isCloning
          ? `Account cloned successfully as "${accountData.accountName}"`
          : "Account added successfully"
      );
      fetchAccountsData(true); // force = true bypasses the Redux cache
      setTimeout(() => {
        closeAccountDialog();
      }, 1500);
    }
  };

  // Handle group submission with API integration
  const handleGroupSubmit = async (groupData, action) => {
    if (!canAddSettings && !canEditSettings) return;

    setUpdateError("");
    setUpdateSuccess("");

    // UPDATED: Treat cloning as adding a new group
    if (action === "edit" && !isCloning) {
      setUpdating(true);
      try {
        // Prepare API payload for group editing
        const editPayload = {
          entityType: "Account",
          groupId: groupData.groupId,
          groupName: groupData.groupName,
          description: groupData.description,
          notes: groupData.notes || "", // ADDED: Include notes in API payload
          groupType: groupData.groupType,
          country: groupData.country?.countryName || groupData.country,
          status: groupData.status,
          accountIds: groupData.accountIds || [],
          editMode: "EditGroup",
        };

        // VALIDATION: Ensure required fields are present
        if (!editPayload.groupId) {
          setUpdateError("Group ID is missing. Cannot update group.");
          setUpdating(false);
          return;
        }

        console.log("Updating group with payload:", editPayload);
        console.log("Group data received:", groupData);

        const result = await editGroupOrAccount(editPayload);

        if (result.success) {
          setUpdateSuccess(
            `Group "${groupData.groupName}" updated successfully`
          );
          // Update Redux store in-place — no API re-fetch needed
          dispatch(updateGroupInStore({ ...groupData }));
          setTimeout(() => {
            closeGroupDialog();
          }, 1500);
        } else {
          setUpdateError(result.message);
        }
      } catch (error) {
        console.error("Error updating group:", error);
        setUpdateError("Failed to update group. Please try again.");
      } finally {
        setUpdating(false);
      }
    } else {
      // Add new group or clone — force-refresh from API (server assigns the new ID)
      setUpdateSuccess(
        isCloning
          ? `Group cloned successfully as "${groupData.groupName}"`
          : "Group added successfully"
      );
      fetchGroupsData(true); // force = true bypasses the Redux cache
      setTimeout(() => {
        closeGroupDialog();
      }, 1500);
    }
  };

  // ADDED: Handle account deletion
  const handleDeleteAccount = async (account) => {
    if (!canDeleteSettings) return;

    setDeleteError("");
    setDeleteSuccess("");
    setDeleting(true);

    try {
      console.log("Deleting account:", account.accountId);

      const result = await removeAccount(account.accountId);

      if (result.success) {
        setDeleteSuccess(
          `Account "${account.accountName}" deleted successfully`
        );

        // Remove from Redux store immediately — no API re-fetch needed
        dispatch(removeAccountFromStore(account.accountId));

        setTimeout(() => {
          setDeleteSuccess(""); // Clear success message
        }, 2000);
      } else {
        setDeleteError(result.message || "Failed to delete account");
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      setDeleteError("An unexpected error occurred while deleting the account");
    } finally {
      setDeleting(false);
      setDeleteConfirmDialog(false);
      setItemToDelete(null);
      setDeleteType("");
    }
  };

  // ADDED: Handle group deletion
  const handleDeleteGroup = async (group) => {
    if (!canDeleteSettings) return;

    setDeleteError("");
    setDeleteSuccess("");
    setDeleting(true);

    try {
      console.log("Deleting group:", group.groupId);

      const result = await removeGroup(group.groupId);

      if (result.success) {
        setDeleteSuccess(`Group "${group.groupName}" deleted successfully`);

        // Remove from Redux store immediately — no API re-fetch needed
        dispatch(removeGroupFromStore(group.groupId));

        setTimeout(() => {
          setDeleteSuccess(""); // Clear success message
        }, 2000);
      } else {
        setDeleteError(result.message || "Failed to delete group");
      }
    } catch (error) {
      console.error("Error deleting group:", error);
      setDeleteError("An unexpected error occurred while deleting the group");
    } finally {
      setDeleting(false);
      setDeleteConfirmDialog(false);
      setItemToDelete(null);
      setDeleteType("");
    }
  };

  // ADDED: Open delete confirmation dialog
  const openDeleteConfirmation = (item, type) => {
    if (!canDeleteSettings) return;

    setItemToDelete(item);
    setDeleteType(type);
    setDeleteConfirmDialog(true);
    setDeleteError("");
    setDeleteSuccess("");
  };

  // ADDED: Close delete confirmation dialog
  const closeDeleteConfirmation = () => {
    setDeleteConfirmDialog(false);
    setItemToDelete(null);
    setDeleteType("");
    setDeleteError("");
  };

  // ADDED: Confirm deletion
  const confirmDelete = () => {
    if (deleteType === "account") {
      handleDeleteAccount(itemToDelete);
    } else if (deleteType === "group") {
      handleDeleteGroup(itemToDelete);
    }
  };

  // ADDED: Component for empty state with better UX
  const EmptyStateComponent = ({
    type,
    title,
    description,
    actionText,
    onAction,
    canAdd,
    isFirstLoad,
    isLoading,
  }) => (
    <Box
      sx={{
        textAlign: "center",
        py: 8,
        px: 3,
        bgcolor: "#f8fafc",
        borderRadius: 2,
        border: "2px dashed #e2e8f0",
      }}
    >
      {type === "accounts" ? (
        <AccountIcon sx={{ fontSize: 64, color: "#94a3b8", mb: 2 }} />
      ) : (
        <GroupIcon sx={{ fontSize: 64, color: "#94a3b8", mb: 2 }} />
      )}

      <Typography
        variant="h6"
        sx={{ fontWeight: 600, color: "#1e293b", mb: 1 }}
      >
        {title}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3, maxWidth: 400, mx: "auto" }}
      >
        {description}
      </Typography>

      {canAdd && !isLoading && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAction}
          sx={{
            bgcolor: "#4B5AB6",
            "&:hover": { bgcolor: "#3a4891" },
            borderRadius: 2,
            px: 3,
            py: 1,
          }}
        >
          {actionText}
        </Button>
      )}

      {!canAdd && (
        <Alert severity="info" sx={{ mt: 2, maxWidth: 400, mx: "auto" }}>
          <Typography variant="body2">
            Contact your administrator to add new {type}.
          </Typography>
        </Alert>
      )}
    </Box>
  );

  // ── Token-expiry urgency helper ─────────────────────────────────────────
  // Returns { level, daysLeft, label, color, bg, icon, tooltip }
  const getExpiryStatus = (expiryStr) => {
    if (!expiryStr) return null;
    const expiry = new Date(expiryStr);
    if (isNaN(expiry.getTime())) return null;
    const now = new Date();
    const msLeft = expiry - now;
    const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) {
      return {
        level: "expired",
        daysLeft,
        label: "Expired",
        color: "#fff",
        bg: "#dc2626",
        border: "#b91c1c",
        icon: <ErrorOutlineIcon sx={{ fontSize: 13 }} />,
        tooltip: `Token expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? "s" : ""} ago`,
      };
    }
    if (daysLeft <= 7) {
      return {
        level: "critical",
        daysLeft,
        label: daysLeft === 0 ? "Today" : `${daysLeft}d left`,
        color: "#7f1d1d",
        bg: "#fef2f2",
        border: "#fca5a5",
        icon: <WarningAmberIcon sx={{ fontSize: 13 }} />,
        tooltip: `⚠️ Expires in ${daysLeft} day${daysLeft !== 1 ? "s" : ""} — renew now`,
      };
    }
    if (daysLeft <= 30) {
      return {
        level: "warning",
        daysLeft,
        label: `${daysLeft}d left`,
        color: "#78350f",
        bg: "#fffbeb",
        border: "#fcd34d",
        icon: <ScheduleIcon sx={{ fontSize: 13 }} />,
        tooltip: `Expires in ${daysLeft} days — consider renewing soon`,
      };
    }
    return {
      level: "ok",
      daysLeft,
      label: `${daysLeft}d left`,
      color: "#14532d",
      bg: "#f0fdf4",
      border: "#86efac",
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />,
      tooltip: `Token valid for ${daysLeft} more days`,
    };
  };

  // UPDATED: Filter and sort accounts based on search term and sort preferences
  const getFilteredAndSortedAccounts = () => {
    // First filter accounts
    let filtered = accounts.filter(
      (account) =>
        account.accountName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.provider.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.connectedBy.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Then sort: manual sort takes priority, otherwise default to nearest expiry first
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];

        // Handle null/undefined values
        if (!aValue) aValue = "";
        if (!bValue) bValue = "";

        // Convert to string for consistent comparison
        aValue = aValue.toString().toLowerCase();
        bValue = bValue.toString().toLowerCase();

        if (sortDirection === "asc") {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      });
    } else {
      // Default: sort by expiry date ascending (nearest expiry at top)
      // Accounts with no expiry date sink to the bottom
      filtered.sort((a, b) => {
        const aHas = a.expiry && !isNaN(new Date(a.expiry).getTime());
        const bHas = b.expiry && !isNaN(new Date(b.expiry).getTime());
        if (!aHas && !bHas) return 0;
        if (!aHas) return 1;  // a has no expiry → goes below b
        if (!bHas) return -1; // b has no expiry → goes below a
        return new Date(a.expiry) - new Date(b.expiry); // nearest first
      });
    }

    return filtered;
  };

  const filteredAccounts = getFilteredAndSortedAccounts();

  // ADDED: Handle sort functionality
  const handleSort = (field) => {
    if (sortField === field) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If clicking a new field, set it as the sort field with ascending direction
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // ADDED: Get sort icon for column headers
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <SortIcon sx={{ fontSize: 16, opacity: 0.5, ml: 0.5 }} />;
    }

    return sortDirection === "asc" ? (
      <KeyboardArrowUpIcon sx={{ fontSize: 16, ml: 0.5 }} />
    ) : (
      <KeyboardArrowDownIcon sx={{ fontSize: 16, ml: 0.5 }} />
    );
  };

  // Filter groups based on search term
  const filteredGroups = groups.filter(
    (group) =>
      group.groupName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.groupType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (group.createdBy &&
        group.createdBy.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // If user doesn't have read access, show access denied message
  if (!canViewSettings) {
    return (
      <Box sx={{ py: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
        <Container maxWidth="xl">
          <Alert
            severity="error"
            sx={{
              borderRadius: 2,
              "& .MuiAlert-message": {
                display: "flex",
                alignItems: "center",
                gap: 1,
              },
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Access Denied
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                You don't have permission to view the Settings module. Please
                contact your administrator for access.
              </Typography>
            </Box>
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            mb: 4,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "#1e293b", mb: 1, flexWrap: "wrap", display: "flex", alignItems: "center", gap: 1 }}
            >
              Settings
              {/* Show access level indicator */}
              <Chip
                label={canAddSettings ? "Full Access" : "Read Only"}
                size="small"
                sx={{
                  bgcolor: canAddSettings ? "#dcfce7" : "#fef3c7",
                  color: canAddSettings ? "#15803d" : "#d97706",
                  fontWeight: 500,
                }}
              />
              {/* Show updating indicator */}
              {updating && (
                <Chip
                  label="Updating..."
                  size="small"
                  sx={{
                    bgcolor: "#e0f2fe",
                    color: "#0277bd",
                    fontWeight: 500,
                  }}
                />
              )}
              {/* ADDED: Show deleting indicator */}
              {deleting && (
                <Chip
                  label="Deleting..."
                  size="small"
                  sx={{
                    bgcolor: "#fce4ec",
                    color: "#c2185b",
                    fontWeight: 500,
                  }}
                />
              )}
              {/* Show loading indicators */}
              {(loadingAccounts || loadingGroups) && (
                <Chip
                  label={
                    loadingAccounts && loadingGroups
                      ? "Loading All..."
                      : loadingAccounts
                      ? "Loading Accounts..."
                      : "Loading Groups..."
                  }
                  size="small"
                  sx={{
                    bgcolor: "#fff3cd",
                    color: "#856404",
                    fontWeight: 500,
                  }}
                />
              )}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {canAddSettings
                ? "Manage account settings and configurations"
                : "View account settings and configurations (Read-only)"}
            </Typography>
          </Box>

          {/* Refresh Button */}
          <Button
            variant="outlined"
            onClick={fetchData}
            disabled={
              loading ||
              updating ||
              loadingAccounts ||
              loadingGroups ||
              deleting
            }
            sx={{ borderRadius: 2, alignSelf: { xs: "flex-end", sm: "auto" } }}
          >
            {loading || loadingAccounts || loadingGroups ? (
              <CircularProgress size={20} />
            ) : (
              "Refresh"
            )}
          </Button>
        </Box>

        {/* Error Alert - Show API load errors from Redux */}
        {(accountsError || groupsError) && !accountsEmpty && !groupsEmpty && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Error Loading Data
            </Typography>
            <Typography variant="body2">{accountsError || groupsError}</Typography>
          </Alert>
        )}

        {/* Update Error Alert */}
        {updateError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Update Failed
            </Typography>
            <Typography variant="body2">{updateError}</Typography>
          </Alert>
        )}

        {/* Update Success Alert */}
        {updateSuccess && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Update Successful
            </Typography>
            <Typography variant="body2">{updateSuccess}</Typography>
          </Alert>
        )}

        {/* ADDED: Delete Error Alert */}
        {deleteError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Delete Failed
            </Typography>
            <Typography variant="body2">{deleteError}</Typography>
          </Alert>
        )}

        {/* ADDED: Delete Success Alert */}
        {deleteSuccess && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Delete Successful
            </Typography>
            <Typography variant="body2">{deleteSuccess}</Typography>
          </Alert>
        )}

        {/* Loading State */}
        {loading && accountsFirstLoad && groupsFirstLoad && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading settings data...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Tabs */}
        <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              bgcolor: "#f8fafc",
              "& .MuiTab-root": { fontWeight: 600, fontSize: "1rem" },
            }}
          >
            <Tab
              icon={<AccountIcon />}
              iconPosition="start"
              label={`Accounts ${
                accounts.length > 0 ? `(${accounts.length})` : ""
              }`}
              sx={{ textTransform: "none" }}
            />
            <Tab
              icon={<GroupIcon />}
              iconPosition="start"
              label={`Groups ${
                groups.length > 0 ? `(${groups.length})` : ""
              }`}
              sx={{ textTransform: "none" }}
            />
          </Tabs>

          {/* Tab Content */}
          <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            {tabValue === 0 && (
              <>
                {/* Accounts Tab - UPDATED: Better empty state handling */}
                <Box
                  sx={{
                    mb: 3,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "stretch", sm: "flex-start" },
                    gap: 2,
                  }}
                >
                  {/* Left: title */}
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Account Management
                      </Typography>
                      {loadingAccounts && <CircularProgress size={16} />}
                    </Box>
                    <Typography variant="body2" sx={{ color: "#64748b", mt: 0.25 }}>
                      Connect and manage your social media accounts
                    </Typography>
                  </Box>

                  {/* Right: rich usage card + Add button */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "stretch", sm: "flex-end" }, gap: 1.5 }}>
                    {accountLimit !== null && (
                      <Box sx={{
                        background: isAccountLimitReached
                          ? "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)"
                          : "linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%)",
                        border: "1.5px solid",
                        borderColor: isAccountLimitReached ? "#fb923c" : "#c7d2fe",
                        borderRadius: 3,
                        px: 2,
                        py: 1,
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { sm: 360 },
                        boxShadow: isAccountLimitReached
                          ? "0 2px 10px rgba(251,146,60,0.15)"
                          : "0 2px 10px rgba(75,90,182,0.1)",
                      }}>
                        {/* Single compact row */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          {/* Icon */}
                          <Box sx={{
                            width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                            background: isAccountLimitReached
                              ? "linear-gradient(135deg, #fed7aa, #fb923c)"
                              : "linear-gradient(135deg, #c7d2fe, #818cf8)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                          }}>
                            <AccountIcon sx={{ fontSize: 15, color: isAccountLimitReached ? "#7c2d12" : "#312e81" }} />
                          </Box>

                          {/* Label + progress */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: "0.67rem", color: isAccountLimitReached ? "#9a3412" : "#3730a3", letterSpacing: "0.07em", textTransform: "uppercase" }}>
                                Account Quota
                              </Typography>
                              <Typography sx={{ fontWeight: 500, fontSize: "0.67rem", color: isAccountLimitReached ? "#ea580c" : "#64748b" }}>
                                {isAccountLimitReached ? "⚠ Limit reached" : `${accountLimit - accounts.length} remaining`}
                              </Typography>
                            </Box>
                            {/* Progress bar */}
                            <Box sx={{ height: 6, borderRadius: 3, bgcolor: isAccountLimitReached ? "#fed7aa" : "#dde3f8", overflow: "hidden", mb: 0.5 }}>
                              <Box sx={{
                                height: "100%",
                                width: `${Math.min((accounts.length / accountLimit) * 100, 100)}%`,
                                borderRadius: 3,
                                background: isAccountLimitReached
                                  ? "linear-gradient(90deg, #fb923c 0%, #ef4444 100%)"
                                  : (accounts.length / accountLimit) >= 0.7
                                  ? "linear-gradient(90deg, #4B5AB6 0%, #f59e0b 100%)"
                                  : "linear-gradient(90deg, #4B5AB6 0%, #818cf8 100%)",
                                transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
                              }} />
                            </Box>
                            <Typography sx={{ fontSize: "0.63rem", color: isAccountLimitReached ? "#ea580c" : "#94a3b8" }}>
                              {isAccountLimitReached
                                ? "Subscription limit reached"
                                : `${Math.round((accounts.length / accountLimit) * 100)}% used · Plan limit: ${accountLimit}`}
                            </Typography>
                          </Box>

                          {/* Count */}
                          <Box sx={{ textAlign: "center", flexShrink: 0 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: "1.45rem", lineHeight: 1, color: isAccountLimitReached ? "#ea580c" : "#1e293b", fontVariantNumeric: "tabular-nums" }}>
                              {accounts.length}
                            </Typography>
                            <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8", fontWeight: 500 }}>/ {accountLimit}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {canAddSettings && accounts.length > 0 && (
                      <Tooltip
                        title={isAccountLimitReached ? `Account limit of ${accountLimit} reached on your current plan. Remove an account to add a new one.` : ""}
                        arrow
                      >
                        <span style={{ width: "100%" }}>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => openAccountDialog()}
                            disabled={updating || loadingAccounts || isAccountLimitReached}
                            fullWidth
                            sx={{
                              bgcolor: "#4B5AB6",
                              "&:hover": { bgcolor: "#3a4891" },
                              "&.Mui-disabled": { bgcolor: "#cbd5e1", color: "#94a3b8" },
                              borderRadius: 2,
                              px: 2.5,
                            }}
                          >
                            Add Account
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                {/* UPDATED: Show empty state or table */}
                {accountsEmpty && !loadingAccounts ? (
                  <EmptyStateComponent
                    type="accounts"
                    title="No Accounts Found"
                    description="Get started by adding your first account. You can connect social media, email, or SMS accounts to manage all your communications in one place."
                    actionText="Add Your First Account"
                    onAction={() => openAccountDialog()}
                    canAdd={canAddSettings && !isAccountLimitReached}
                    isFirstLoad={accountsFirstLoad}
                    isLoading={loadingAccounts}
                  />
                ) : (
                  <>
                    {/* Search - Show only if there are accounts */}
                    {accounts.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <TextField
                          fullWidth
                          placeholder="Search accounts..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <SearchIcon sx={{ color: "#94a3b8", mr: 1 }} />
                            ),
                          }}
                          sx={{ bgcolor: "#fff", maxWidth: 400 }}
                        />
                      </Box>
                    )}

                    {/* Accounts Table - UPDATED: Better loading and no results states */}
                    <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: "auto" }}>
                      <Table>
                        <TableHead sx={{ bgcolor: "#f8fafc" }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Account ID
                            </TableCell>
                            {/* UPDATED: Add sorting to Type column */}
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                cursor: "pointer",
                                userSelect: "none",
                                "&:hover": { bgcolor: "#f1f5f9" },
                                display: "flex",
                                alignItems: "center",
                                minHeight: 56,
                              }}
                              onClick={() => handleSort("type")}
                              title="Click to sort by Type"
                            >
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                Type
                                {getSortIcon("type")}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Provider
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Account Name
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Identifier
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Connected By
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Group
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Link</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Status
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Token Expiry
                            </TableCell>
                            {canEditSettings && (
                              <TableCell sx={{ fontWeight: 600 }}>
                                Actions
                              </TableCell>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loadingAccounts ? (
                            <TableRow>
                              <TableCell
                                colSpan={canEditSettings ? 11 : 10}
                                sx={{ textAlign: "center", py: 6 }}
                              >
                                <CircularProgress size={32} />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 2 }}
                                >
                                  Loading accounts data...
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : filteredAccounts.length === 0 &&
                            accounts.length > 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={canEditSettings ? 11 : 10}
                                sx={{ textAlign: "center", py: 6 }}
                              >
                                <SearchIcon
                                  sx={{ fontSize: 48, color: "#94a3b8", mb: 2 }}
                                />
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 600,
                                    color: "#1e293b",
                                    mb: 1,
                                  }}
                                >
                                  No Results Found
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 2 }}
                                >
                                  No accounts match your search criteria "
                                  {searchTerm}"
                                </Typography>
                                <Button
                                  variant="outlined"
                                  onClick={() => setSearchTerm("")}
                                  size="small"
                                >
                                  Clear Search
                                </Button>
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredAccounts.map((account) => (
                              <TableRow key={account.id}>
                                <TableCell
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  {account.accountId}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={account.type}
                                    size="small"
                                    sx={{
                                      bgcolor:
                                        account.type === "Social Media"
                                          ? "#e0f2fe"
                                          : account.type === "Email"
                                          ? "#f3e5f5"
                                          : "#e8f5e8",
                                      color:
                                        account.type === "Social Media"
                                          ? "#0277bd"
                                          : account.type === "Email"
                                          ? "#7b1fa2"
                                          : "#2e7d32",
                                    }}
                                  />
                                </TableCell>
                                <TableCell>{account.provider}</TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    {account.accountName}
                                    {account.notes && (
                                      <Chip
                                        label="📝"
                                        size="small"
                                        sx={{
                                          bgcolor: "#fff3cd",
                                          color: "#856404",
                                          minWidth: "auto",
                                          height: 20,
                                          "& .MuiChip-label": { px: 0.5 },
                                        }}
                                        title="Has notes"
                                      />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      maxWidth: 150,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {account.identifier}
                                  </Typography>
                                </TableCell>
                                <TableCell>{account.connectedBy}</TableCell>
                                <TableCell>
                                  {account.group ? (
                                    <Chip
                                      label={account.group}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ) : (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      No group
                                    </Typography>
                                  )}
                                </TableCell>

                                                                <TableCell>
                                  {account.appLink ? (
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        window.open(
                                          account.appLink,
                                          "_blank",
                                          "noopener,noreferrer"
                                        )
                                      }
                                      sx={{ color: "#4B5AB6" }}
                                      title={account.appLink}
                                    >
                                      <LinkIcon />
                                    </IconButton>
                                  ) : (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      -
                                    </Typography>
                                  )}
                                </TableCell>


                                <TableCell>
                                  <Chip
                                    label={account.status}
                                    size="small"
                                    color={
                                      account.status === "Active"
                                        ? "success"
                                        : "default"
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const status = getExpiryStatus(account.expiry);
                                    if (!account.expiry) {
                                      return (
                                        <Typography variant="body2" color="text.secondary">N/A</Typography>
                                      );
                                    }
                                    return (
                                      <Tooltip title={status ? status.tooltip : account.expiry} arrow>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                                          {status && (
                                            <Box
                                              sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 0.4,
                                                px: 0.85,
                                                py: 0.35,
                                                borderRadius: "6px",
                                                border: `1px solid ${status.border}`,
                                                background: status.bg,
                                                color: status.color,
                                                fontWeight: 700,
                                                fontSize: "0.7rem",
                                                width: "fit-content",
                                                whiteSpace: "nowrap",
                                                animation: status.level === "critical" || status.level === "expired"
                                                  ? "pulse 2s ease-in-out infinite"
                                                  : "none",
                                                "@keyframes pulse": {
                                                  "0%, 100%": { opacity: 1 },
                                                  "50%": { opacity: 0.65 },
                                                },
                                              }}
                                            >
                                              {status.icon}
                                              {status.label}
                                            </Box>
                                          )}
                                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem", lineHeight: 1.2 }}>
                                            {new Date(account.expiry).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}
                                          </Typography>
                                        </Box>
                                      </Tooltip>
                                    );
                                  })()}
                                </TableCell>
                                {canEditSettings && (
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={() => openAccountDialog(account)}
                                      disabled={
                                        updating || loadingAccounts || deleting
                                      }
                                      sx={{ color: "#4B5AB6" }}
                                      title="Edit Account"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    {/* ADDED: Clone button */}
                                    {canAddSettings && (
                                      <Tooltip
                                        title={isAccountLimitReached ? `Account limit of ${accountLimit} reached — remove an account to clone` : "Clone Account"}
                                        arrow
                                      >
                                        <span>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              openAccountDialog(account, true)
                                            }
                                            disabled={
                                              updating ||
                                              loadingAccounts ||
                                              deleting ||
                                              isAccountLimitReached
                                            }
                                            sx={{
                                              color: isAccountLimitReached ? "#cbd5e1" : "#059669",
                                            }}
                                          >
                                            <ContentCopyIcon />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    )}
                                    {canDeleteSettings && (
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          openDeleteConfirmation(
                                            account,
                                            "account"
                                          )
                                        }
                                        disabled={
                                          updating ||
                                          loadingAccounts ||
                                          deleting
                                        }
                                        sx={{ color: "#dc2626" }}
                                        title="Delete Account"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    )}
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        openViewDialog(account, "account")
                                      }
                                      disabled={
                                        updating || loadingAccounts || deleting
                                      }
                                      sx={{ color: "#4B5AB6" }}
                                      title="View Account"
                                    >
                                      <VisibilityIcon />
                                    </IconButton>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            )}

            {tabValue === 1 && (
              <>
                {/* Account Groups Tab - UPDATED: Better empty state handling */}
                <Box
                  sx={{
                    mb: 3,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "stretch", sm: "flex-start" },
                    gap: 2,
                  }}
                >
                  {/* Left: title */}
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Groups Management
                      </Typography>
                      {loadingGroups && <CircularProgress size={16} />}
                    </Box>
                    <Typography variant="body2" sx={{ color: "#64748b", mt: 0.25 }}>
                      Organise accounts into groups for campaigns
                    </Typography>
                  </Box>

                  {/* Right: rich usage card + Add button */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "stretch", sm: "flex-end" }, gap: 1.5 }}>
                    {groupLimit !== null && (
                      <Box sx={{
                        background: isGroupLimitReached
                          ? "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)"
                          : "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                        border: "1.5px solid",
                        borderColor: isGroupLimitReached ? "#fb923c" : "#86efac",
                        borderRadius: 3,
                        px: 2,
                        py: 1,
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { sm: 360 },
                        boxShadow: isGroupLimitReached
                          ? "0 2px 10px rgba(251,146,60,0.15)"
                          : "0 2px 10px rgba(22,163,74,0.08)",
                      }}>
                        {/* Single compact row */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          {/* Icon */}
                          <Box sx={{
                            width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                            background: isGroupLimitReached
                              ? "linear-gradient(135deg, #fed7aa, #fb923c)"
                              : "linear-gradient(135deg, #bbf7d0, #4ade80)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                          }}>
                            <GroupIcon sx={{ fontSize: 15, color: isGroupLimitReached ? "#7c2d12" : "#14532d" }} />
                          </Box>

                          {/* Label + progress */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: "0.67rem", color: isGroupLimitReached ? "#9a3412" : "#14532d", letterSpacing: "0.07em", textTransform: "uppercase" }}>
                                Group Quota
                              </Typography>
                              <Typography sx={{ fontWeight: 500, fontSize: "0.67rem", color: isGroupLimitReached ? "#ea580c" : "#64748b" }}>
                                {isGroupLimitReached ? "⚠ Limit reached" : `${groupLimit - groups.length} remaining`}
                              </Typography>
                            </Box>
                            {/* Progress bar */}
                            <Box sx={{ height: 6, borderRadius: 3, bgcolor: isGroupLimitReached ? "#fed7aa" : "#bbf7d0", overflow: "hidden", mb: 0.5 }}>
                              <Box sx={{
                                height: "100%",
                                width: `${Math.min((groups.length / groupLimit) * 100, 100)}%`,
                                borderRadius: 3,
                                background: isGroupLimitReached
                                  ? "linear-gradient(90deg, #fb923c 0%, #ef4444 100%)"
                                  : (groups.length / groupLimit) >= 0.7
                                  ? "linear-gradient(90deg, #22c55e 0%, #f59e0b 100%)"
                                  : "linear-gradient(90deg, #22c55e 0%, #16a34a 100%)",
                                transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
                              }} />
                            </Box>
                            <Typography sx={{ fontSize: "0.63rem", color: isGroupLimitReached ? "#ea580c" : "#94a3b8" }}>
                              {isGroupLimitReached
                                ? "Subscription limit reached"
                                : `${Math.round((groups.length / groupLimit) * 100)}% used · Plan limit: ${groupLimit}`}
                            </Typography>
                          </Box>

                          {/* Count */}
                          <Box sx={{ textAlign: "center", flexShrink: 0 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: "1.45rem", lineHeight: 1, color: isGroupLimitReached ? "#ea580c" : "#1e293b", fontVariantNumeric: "tabular-nums" }}>
                              {groups.length}
                            </Typography>
                            <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8", fontWeight: 500 }}>/ {groupLimit}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {canAddSettings && groups.length > 0 && (
                      <Tooltip
                        title={isGroupLimitReached ? `Group limit of ${groupLimit} reached on your current plan. Remove a group to add a new one.` : ""}
                        arrow
                      >
                        <span style={{ width: "100%" }}>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => openGroupDialog()}
                            disabled={updating || loadingGroups || isGroupLimitReached}
                            fullWidth
                            sx={{
                              bgcolor: "#4B5AB6",
                              "&:hover": { bgcolor: "#3a4891" },
                              "&.Mui-disabled": { bgcolor: "#cbd5e1", color: "#94a3b8" },
                              borderRadius: 2,
                            }}
                          >
                            Add Group
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                {/* UPDATED: Show empty state or table */}
                {groupsEmpty && !loadingGroups ? (
                  <EmptyStateComponent
                    type="groups"
                    title="No Groups Found"
                    description="Organize your accounts by creating groups. Groups help you manage multiple accounts together for campaigns, regions, or different business units."
                    actionText="Create Your First Group"
                    onAction={() => openGroupDialog()}
                    canAdd={canAddSettings && !isGroupLimitReached}
                    isFirstLoad={groupsFirstLoad}
                    isLoading={loadingGroups}
                  />
                ) : (
                  <>
                    {/* Search - Show only if there are groups */}
                    {groups.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <TextField
                          fullWidth
                          placeholder="Search groups..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <SearchIcon sx={{ color: "#94a3b8", mr: 1 }} />
                            ),
                          }}
                          sx={{ bgcolor: "#fff", maxWidth: 400 }}
                        />
                      </Box>
                    )}

                    {/* Groups Table - Similar improvements as accounts table */}
                    <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: "auto" }}>
                      <Table>
                        <TableHead sx={{ bgcolor: "#f8fafc" }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Group ID
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Group Name
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Group Type
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Description
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Country
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Accounts Count
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Status
                            </TableCell>
                            {canEditSettings && (
                              <TableCell sx={{ fontWeight: 600 }}>
                                Actions
                              </TableCell>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loadingGroups ? (
                            <TableRow>
                              <TableCell
                                colSpan={canEditSettings ? 8 : 7}
                                sx={{ textAlign: "center", py: 6 }}
                              >
                                <CircularProgress size={32} />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 2 }}
                                >
                                  Loading groups data...
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : filteredGroups.length === 0 &&
                            groups.length > 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={canEditSettings ? 8 : 7}
                                sx={{ textAlign: "center", py: 6 }}
                              >
                                <SearchIcon
                                  sx={{ fontSize: 48, color: "#94a3b8", mb: 2 }}
                                />
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 600,
                                    color: "#1e293b",
                                    mb: 1,
                                  }}
                                >
                                  No Results Found
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 2 }}
                                >
                                  No groups match your search criteria "
                                  {searchTerm}"
                                </Typography>
                                <Button
                                  variant="outlined"
                                  onClick={() => setSearchTerm("")}
                                  size="small"
                                >
                                  Clear Search
                                </Button>
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredGroups.map((group) => (
                              <TableRow key={group.id}>
                                <TableCell
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  {group?.groupId}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    {group.groupName}
                                    {group.notes && (
                                      <Chip
                                        label="📝"
                                        size="small"
                                        sx={{
                                          bgcolor: "#fff3cd",
                                          color: "#856404",
                                          minWidth: "auto",
                                          height: 20,
                                          "& .MuiChip-label": { px: 0.5 },
                                        }}
                                        title="Has notes"
                                      />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={group.groupType}
                                    size="small"
                                    sx={{
                                      bgcolor:
                                        group.groupType === "Mixed"
                                          ? "#fff3cd"
                                          : "#e8f5e8",
                                      color:
                                        group.groupType === "Mixed"
                                          ? "#856404"
                                          : "#2e7d32",
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      maxWidth: 200,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {group.description || "No description"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {group.country?.countryName || "N/A"}
                                  </Typography>
                                  {group.country?.countryCode &&
                                    group.country.countryCode !== "N/A" && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        ({group.country.countryCode})
                                      </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 500 }}
                                    >
                                      {group.accountsCount}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {group.accountsCount === 1
                                        ? "account"
                                        : "accounts"}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={group.status}
                                    size="small"
                                    color={
                                      group.status === "Active"
                                        ? "success"
                                        : "default"
                                    }
                                  />
                                </TableCell>
                                {canEditSettings && (
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={() => openGroupDialog(group)}
                                      disabled={
                                        updating || loadingGroups || deleting
                                      }
                                      sx={{ color: "#4B5AB6" }}
                                      title="Edit Group"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    {/* ADDED: Clone button */}
                                    {canAddSettings && (
                                      <Tooltip
                                        title={isGroupLimitReached ? `Group limit of ${groupLimit} reached — remove a group to clone` : "Clone Group"}
                                        arrow
                                      >
                                        <span>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              openGroupDialog(group, true)
                                            }
                                            disabled={
                                              updating || loadingGroups || deleting ||
                                              isGroupLimitReached
                                            }
                                            sx={{
                                              color: isGroupLimitReached ? "#cbd5e1" : "#059669",
                                            }}
                                          >
                                            <ContentCopyIcon />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    )}
                                    {canDeleteSettings && (
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          openDeleteConfirmation(group, "group")
                                        }
                                        disabled={
                                          updating || loadingGroups || deleting
                                        }
                                        sx={{ color: "#dc2626" }}
                                        title="Delete Group"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    )}
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        openViewDialog(group, "group")
                                      }
                                      disabled={
                                        updating || loadingGroups || deleting
                                      }
                                      sx={{ color: "#4B5AB6" }}
                                      title="View Group"
                                    >
                                      <VisibilityIcon />
                                    </IconButton>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            )}
          </Box>
        </Paper>

        {/* ── Danger Zone — Admin only, hidden by default, Accounts tab only ── */}
        {isAdmin && tabValue === 0 && (
          <Box sx={{ mt: 6, mb: 3 }}>
            {/* Subtle toggle — looks like a quiet footer link, not alarming */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                opacity: 0.38,
                transition: "opacity 0.2s",
                userSelect: "none",
                "&:hover": { opacity: 0.65 },
              }}
              onClick={() => setDangerZoneOpen((v) => !v)}
            >
              <Typography sx={{ fontSize: "0.72rem", color: "#64748b", letterSpacing: "0.04em" }}>
                Account options
              </Typography>
              {dangerZoneOpen
                ? <KeyboardArrowUpIcon sx={{ fontSize: 14, color: "#64748b" }} />
                : <KeyboardArrowDownIcon sx={{ fontSize: 14, color: "#64748b" }} />}
            </Box>

            {/* Collapsible Danger Zone — only visible after click */}
            <Collapse in={dangerZoneOpen}>
              <Box
                sx={{
                  mt: 2,
                  border: "1.5px solid #fca5a5",
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                {/* Header */}
                <Box sx={{
                  bgcolor: "#fef2f2",
                  px: 3, py: 1.5,
                  borderBottom: "1px solid #fca5a5",
                  display: "flex", alignItems: "center", gap: 1,
                }}>
                  <WarningAmberIcon sx={{ color: "#dc2626", fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#dc2626", letterSpacing: "0.04em" }}>
                    Danger Zone
                  </Typography>
                </Box>

                {/* Close Account row */}
                <Box sx={{
                  bgcolor: "#fff",
                  px: 3, py: 2.5,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  flexWrap: "wrap", gap: 2,
                }}>
                  <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1e293b", mb: 0.4 }}>
                      Permanently close your account
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", color: "#64748b", lineHeight: 1.5 }}>
                      This will <strong>permanently delete your login account</strong> and end all access to this
                      product. All your data — posts, schedules, accounts, and settings — will be erased
                      forever. You will <strong>not</strong> be able to log in again.
                      This action <strong>cannot be undone</strong>.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() => setCloseMyAccountOpen(true)}
                    sx={{
                      color: "#dc2626",
                      borderColor: "#dc2626",
                      fontWeight: 700,
                      borderRadius: 2,
                      px: 3,
                      whiteSpace: "nowrap",
                      "&:hover": { bgcolor: "#fef2f2", borderColor: "#b91c1c", color: "#b91c1c" },
                    }}
                  >
                    Close My Account
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Fullscreen loader while closing account */}
        {closingAccount && (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              bgcolor: "rgba(15, 23, 42, 0.82)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              backdropFilter: "blur(6px)",
            }}
          >
            <CircularProgress size={72} thickness={3} sx={{ color: "#ef4444" }} />
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.25rem", mb: 0.5 }}>
                Closing your account…
              </Typography>
              <Typography sx={{ color: "#94a3b8", fontSize: "0.85rem" }}>
                Please wait. You will be redirected to the login page.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Dialog Components */}
        {canAddSettings && (
          <>
            <AddNewAccount
              open={accountDialog}
              onClose={closeAccountDialog}
              onSubmit={handleAccountSubmit}
              editingAccount={editingAccount}
              isCloning={isCloning} // ADDED: Pass clone flag
              accountTypes={accountTypes}
              providers={providers}
              initialProvider={initialProvider}
            />

            <AddNewGroup
              open={groupDialog}
              onClose={closeGroupDialog}
              onSubmit={handleGroupSubmit}
              editingGroup={editingGroup}
              isCloning={isCloning} // ADDED: Pass clone flag
              groupTypes={groupTypes}
            />
          </>
        )}

        {/* UPDATED: Replace the entire ViewOnlyDialog with the new component */}
        <ViewDetailsModal
          open={viewDialog}
          onClose={closeViewDialog}
          viewingItem={viewingItem}
          viewType={viewType}
          canEditSettings={canEditSettings}
          onEdit={handleEditFromView}
        />

        {/* ── Close Account Confirmation Dialog ── */}
        <Dialog
          open={closeMyAccountOpen}
          onClose={() => { if (!closingAccount) { setCloseMyAccountOpen(false); setCloseAccountConfirmText(""); setCloseAccountError(""); } }}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
            <Box sx={{
              width: 38, height: 38, borderRadius: "50%",
              bgcolor: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <ErrorOutlineIcon sx={{ color: "#dc2626", fontSize: 22 }} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#1e293b" }}>
              Close My Account Permanently
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>You are about to permanently close your login account.</Typography>
              <Typography variant="body2">Once confirmed, your login access will be revoked and all data — including posts, scheduled campaigns, connected accounts, groups, and settings — will be <strong>permanently erased</strong>. You will have no further relationship with this product.</Typography>
            </Alert>

            {closeAccountError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {closeAccountError}
              </Alert>
            )}

            <Typography sx={{ fontSize: "0.82rem", color: "#475569", mb: 1.5 }}>
              To confirm, type <strong style={{ color: "#dc2626" }}>DELETE MY ACCOUNT</strong> in the box below:
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Type DELETE MY ACCOUNT to confirm"
              value={closeAccountConfirmText}
              onChange={(e) => setCloseAccountConfirmText(e.target.value)}
              disabled={closingAccount}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&.Mui-focused fieldset": { borderColor: "#dc2626" },
                },
              }}
            />
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
            <Button
              onClick={() => { setCloseMyAccountOpen(false); setCloseAccountConfirmText(""); setCloseAccountError(""); }}
              disabled={closingAccount}
              sx={{ color: "#64748b", fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={closeAccountConfirmText !== "DELETE MY ACCOUNT" || closingAccount}
              onClick={handleCloseAccount}
              startIcon={closingAccount ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
              sx={{
                bgcolor: "#dc2626",
                fontWeight: 700,
                borderRadius: 2,
                px: 3,
                "&:hover": { bgcolor: "#b91c1c" },
                "&.Mui-disabled": { bgcolor: "#fca5a5", color: "#fff" },
              }}
            >
              {closingAccount ? "Closing…" : "Yes, Permanently Close My Account"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ADDED: Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmDialog}
          onClose={closeDeleteConfirmation}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle
            sx={{
              fontWeight: 600,
              bgcolor: "#fef2f2",
              color: "#dc2626",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <DeleteIcon />
            Confirm Deletion
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {/* Delete Error Alert inside dialog */}
            {deleteError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {deleteError}
              </Alert>
            )}

            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete this {deleteType}?
            </Typography>

            {itemToDelete && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#f8fafc",
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "#1e293b" }}
                >
                  {deleteType === "account"
                    ? "Account Details:"
                    : "Group Details:"}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>ID:</strong>{" "}
                  {deleteType === "account"
                    ? itemToDelete.accountId
                    : itemToDelete?.groupId}
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong>{" "}
                  {deleteType === "account"
                    ? itemToDelete.accountName
                    : itemToDelete.groupName}
                </Typography>
                <Typography variant="body2">
                  <strong>Type:</strong>{" "}
                  {deleteType === "account"
                    ? itemToDelete.type
                    : itemToDelete.groupType}
                </Typography>
                {deleteType === "group" && (
                  <Typography variant="body2">
                    <strong>Accounts:</strong> {itemToDelete.accountsCount}{" "}
                    account{itemToDelete.accountsCount !== 1 ? "s" : ""}
                  </Typography>
                )}
              </Box>
            )}

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Warning:</strong> This action cannot be undone.
                {deleteType === "group" &&
                  itemToDelete?.accountsCount > 0 &&
                  ` This group contains ${itemToDelete.accountsCount} account${
                    itemToDelete.accountsCount !== 1 ? "s" : ""
                  }.`}
              </Typography>
            </Alert>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0, justifyContent: "space-between" }}>
            <Button
              onClick={closeDeleteConfirmation}
              disabled={deleting}
              sx={{ color: "#64748b" }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              variant="contained"
              disabled={deleting}
              sx={{
                bgcolor: "#dc2626",
                "&:hover": { bgcolor: "#b91c1c" },
                px: 3,
              }}
            >
              {deleting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                  Deleting...
                </>
              ) : (
                <>
                  <DeleteIcon sx={{ mr: 1 }} />
                  Delete {deleteType === "account" ? "Account" : "Group"}
                </>
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Settings;
